// src/components/UpdateAvailableBanner.tsx
//
// Watches the service worker for new versions and shows a small banner
// inviting the user to reload. Fixes a recurring "I shipped a fix but
// the boss's tab is still running the old JS" problem (2026-05-25 PT —
// plate-corrector badge logic shipped to prod but boss didn't see it
// flip from "Failed" to "Fixed" because his open tab was running the
// previous bundle).
//
// Mounted once in App. No dependencies on any state store so it stays
// safe to render before the auth / map / etc. trees are ready.

import { useEffect, useState } from 'react';

export function UpdateAvailableBanner() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    let cancelled = false;

    void navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg || cancelled) return;

      // If a waiting worker already exists at boot, surface immediately.
      if (reg.waiting && navigator.serviceWorker.controller) {
        setWaitingWorker(reg.waiting);
        setHasUpdate(true);
      }

      // Watch for new versions installing in the background.
      reg.addEventListener('updatefound', () => {
        const installing = reg.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(installing);
            setHasUpdate(true);
          }
        });
      });

      // Poll every 5 minutes for a new SW so users on long-running tabs
      // (cockpit pilots leave the tab open all day) notice without
      // having to navigate. Browsers also do periodic checks but the
      // explicit poll is cheap insurance.
      const interval = window.setInterval(() => {
        if (!cancelled) reg.update().catch(() => undefined);
      }, 5 * 60 * 1000);
      return () => window.clearInterval(interval);
    });

    // When the active SW changes (because waiting → activated), reload
    // so the React app reboots into the new JS bundle. Without this,
    // a manual skipWaiting() leaves the tab running mismatched JS.
    let didReload = false;
    const onControllerChange = () => {
      if (didReload) return;
      didReload = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  function reloadNow() {
    if (!waitingWorker) {
      window.location.reload();
      return;
    }
    // Tell the waiting SW to take over; controllerchange listener above
    // then triggers the actual reload.
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }

  if (!hasUpdate) return null;

  return (
    <div
      role="alert"
      className="fixed left-1/2 z-[9999] -translate-x-1/2 rounded-full border border-sky-500/60 bg-sky-900/95 px-4 py-2 text-sm text-sky-100 shadow-lg backdrop-blur flex items-center gap-3"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
    >
      <span>
        <span className="font-semibold">New version available</span>
        <span className="ml-1 text-sky-200/80">— reload to pick up the latest fixes.</span>
      </span>
      <button
        onClick={reloadNow}
        className="rounded bg-sky-600 px-3 py-1 text-xs font-semibold text-white hover:bg-sky-500 transition-colors"
      >
        Reload now
      </button>
      <button
        onClick={() => setHasUpdate(false)}
        className="text-sky-300 hover:text-sky-100"
        aria-label="Dismiss update banner"
        title="Dismiss"
      >
        ✕
      </button>
    </div>
  );
}
